import { Project, SyntaxKind, PropertyAssignment } from 'ts-morph'
import * as path from 'path'
import type { Dependencies } from './types'
import { formatCode } from '../generators/utils'

/**
 * ConfigWriter handles writing updates to agent.config.ts
 *
 * Note: For reading dependencies, use AgentProject.dependencies instead.
 * This class is only for surgical updates to the dependencies field.
 */
export class ConfigWriter {
  private configPath: string

  constructor(projectPath: string) {
    this.configPath = path.join(projectPath, 'agent.config.ts')
  }

  /**
   * Updates the dependencies field in agent.config.ts
   * Uses ts-morph to surgically update only the dependencies property
   * while preserving all other config fields (name, models, state, etc.)
   */
  async updateDependencies(dependencies: Dependencies): Promise<void> {
    const project = new Project()
    const sourceFile = project.addSourceFileAtPath(this.configPath)

    // Find the defineConfig() call
    const defineConfigCall = sourceFile.getDescendantsOfKind(SyntaxKind.CallExpression).find((call) => {
      const expression = call.getExpression()
      return expression.getText() === 'defineConfig'
    })

    if (!defineConfigCall) {
      throw new Error('Could not find defineConfig() call in agent.config.ts')
    }

    // Get the first argument (config object)
    const configArg = defineConfigCall.getArguments()[0]
    if (!configArg || !configArg.isKind(SyntaxKind.ObjectLiteralExpression)) {
      throw new Error('defineConfig() must have an object literal as its first argument')
    }

    const configObject = configArg.asKind(SyntaxKind.ObjectLiteralExpression)
    if (!configObject) {
      throw new Error('Could not find dependencies property in agent.config.ts')
    }

    // Find or create the dependencies property
    let dependenciesProperty = configObject.getProperty('dependencies') as PropertyAssignment | undefined

    if (dependenciesProperty) {
      // Update existing property
      dependenciesProperty.setInitializer(JSON.stringify(dependencies))
    } else {
      // Add new property at the end
      dependenciesProperty = configObject.addPropertyAssignment({
        name: 'dependencies',
        initializer: JSON.stringify(dependencies),
      })
    }

    // Format the entire source file to ensure consistent indentation
    sourceFile.formatText()

    // Apply Prettier formatting to respect project .prettierrc
    const content = sourceFile.getFullText()
    const formatted = await formatCode(content, this.configPath)
    sourceFile.replaceWithText(formatted)

    // Save the file
    await sourceFile.save()
  }
}
